import apiClient from './api';
import { AUTH_CONFIG } from '@/config/api';
import type { 
  LoginCredentials, 
  RegisterData, 
  AuthResponse, 
  User 
} from '@/types';

export const authService = {
  /**
   * Login with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const { data } = await apiClient.post<AuthResponse>('/login', credentials);
    
    // Store tokens and user
    this.storeAuthData(data);
    
    return data;
  },

  /**
   * Register new user
   */
  async register(userData: RegisterData): Promise<AuthResponse> {
    const { data } = await apiClient.post<AuthResponse>('/register', userData);
    
    // Store tokens and user
    this.storeAuthData(data);
    
    return data;
  },

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/logout');
    } finally {
      this.clearAuthData();
    }
  },

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<User> {
    const { data } = await apiClient.get<User>('/user');
    
    // Update stored user
    localStorage.setItem(AUTH_CONFIG.userKey, JSON.stringify(data));
    
    return data;
  },

  /**
   * Refresh access token
   */
  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem(AUTH_CONFIG.refreshTokenKey);
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const { data } = await apiClient.post<AuthResponse>('/refresh', {
      refresh_token: refreshToken,
    });

    // Update tokens
    localStorage.setItem(AUTH_CONFIG.tokenKey, data.access_token);
    if (data.refresh_token) {
      localStorage.setItem(AUTH_CONFIG.refreshTokenKey, data.refresh_token);
    }

    return data;
  },

  /**
   * Request password reset
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    const { data } = await apiClient.post('/password/email', { email });
    return data;
  },

  /**
   * Reset password with token
   */
  async resetPassword(
    token: string, 
    email: string, 
    password: string, 
    passwordConfirmation: string
  ): Promise<{ message: string }> {
    const { data } = await apiClient.post('/password/reset', {
      token,
      email,
      password,
      password_confirmation: passwordConfirmation,
    });
    return data;
  },

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<{ message: string }> {
    const { data } = await apiClient.post(`/email/verify/${token}`);
    return data;
  },

  /**
   * Resend email verification
   */
  async resendVerification(): Promise<{ message: string }> {
    const { data } = await apiClient.post('/email/resend');
    return data;
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem(AUTH_CONFIG.tokenKey);
    return !!token;
  },

  /**
   * Get stored access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(AUTH_CONFIG.tokenKey);
  },

  /**
   * Get stored user
   */
  getStoredUser(): User | null {
    const userStr = localStorage.getItem(AUTH_CONFIG.userKey);
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  },

  /**
   * Store authentication data
   */
  storeAuthData(data: AuthResponse): void {
    localStorage.setItem(AUTH_CONFIG.tokenKey, data.access_token);
    localStorage.setItem(AUTH_CONFIG.refreshTokenKey, data.refresh_token);
    localStorage.setItem(AUTH_CONFIG.userKey, JSON.stringify(data.user));
    
    // Store token expiry if available
    if (data.expires_in) {
      const expiresAt = Date.now() + data.expires_in * 1000;
      localStorage.setItem(AUTH_CONFIG.tokenExpiryKey, expiresAt.toString());
    }
  },

  /**
   * Clear all authentication data
   */
  clearAuthData(): void {
    localStorage.removeItem(AUTH_CONFIG.tokenKey);
    localStorage.removeItem(AUTH_CONFIG.refreshTokenKey);
    localStorage.removeItem(AUTH_CONFIG.userKey);
    localStorage.removeItem(AUTH_CONFIG.tokenExpiryKey);
  },
};
